import React, { useEffect, useState } from "react";
import { Plus, Trash2, UserSquare2, CalendarIcon, Upload, ShieldCheck, Briefcase, Link2 } from "lucide-react";
import { format, parseISO } from "date-fns";
import api, { fileUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function DobPicker({ value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  let date;
  try { date = value ? parseISO(value) : undefined; } catch { date = undefined; }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" data-testid={testid} className="mt-1.5 w-full justify-start font-normal">
          <CalendarIcon className="h-4 w-4 mr-2 opacity-70" />
          {date ? format(date, "dd/MM/yyyy") : <span className="text-muted-foreground">Select date</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={date} defaultMonth={date}
          captionLayout="dropdown-buttons" fromYear={1930} toYear={new Date().getFullYear() + 1}
          onSelect={(d) => { onChange(d ? format(d, "yyyy-MM-dd") : ""); setOpen(false); }} />
      </PopoverContent>
    </Popover>
  );
}

function FileField({ value, onChange, testid }) {
  const onFile = (e) => {
    const f = (e.target.files || [])[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => onChange(r.result);
    r.readAsDataURL(f);
  };
  const isData = typeof value === "string" && value.startsWith("data:");
  const hasFile = value && value.length;
  return (
    <div className="mt-1.5 flex items-center gap-3 flex-wrap">
      <label className="flex items-center gap-2 text-sm underline cursor-pointer">
        <Upload className="h-4 w-4" />{hasFile ? "Replace file" : "Upload file"}
        <input type="file" className="hidden" data-testid={testid} onChange={onFile} />
      </label>
      {hasFile && !isData && <a href={fileUrl(value)} target="_blank" rel="noreferrer" className="text-xs underline text-[#D98324]">View uploaded file</a>}
      {hasFile && isData && <span className="text-xs text-muted-foreground">File ready to submit</span>}
      {hasFile && <button type="button" className="text-xs text-[#9E382A] underline" onClick={() => onChange("")}>Remove</button>}
    </div>
  );
}

// Export so pages can block submit until required landlord fields are filled.
export function onboardingMissing(schema, intake, mode = "landlord") {
  const miss = [];
  (schema?.sections || []).forEach((sec) => (sec.fields || []).forEach((f) => {
    if (mode === "landlord" && f.filled_by === "business") return;
    if (!f.required || f.type === "info") return;
    const v = intake[f.key];
    if (f.type === "bool" || f.type === "acknowledge") { if (!v) miss.push(f.label); }
    else if (v === undefined || v === null || (typeof v === "string" && !v.trim())) miss.push(f.label);
  }));
  return miss;
}

const DIR_SYNC = {
  "property.cleaner_name": ["cleaner", "name"], "property.cleaner_email": ["cleaner", "email"],
  "property.contractor_name": ["contractor", "name"], "property.contractor_email": ["contractor", "email"],
};

function DirectoryPick({ f, dir, intake, set }) {
  const cur = dir.list.findIndex((p) => (p[dir.prop] || "").toLowerCase() === String(intake[f.key] || "").toLowerCase());
  const pick = (i) => {
    const p = dir.list[Number(i)];
    const next = { ...intake };
    if (dir.keys.name) next[dir.keys.name] = p.name;
    if (dir.keys.email && p.email) next[dir.keys.email] = p.email;
    next[f.key] = p[dir.prop] || next[f.key];
    set(next);
  };
  return (
    <Select value={cur >= 0 ? String(cur) : ""} onValueChange={pick}>
      <SelectTrigger data-testid={`dir-pick-${f.key}`} className="mt-1.5 h-8 text-xs bg-secondary/40">
        <SelectValue placeholder={`Pick a saved ${dir.kind}…`} />
      </SelectTrigger>
      <SelectContent>
        {dir.list.map((p, i) => (
          <SelectItem key={i} value={String(i)} data-testid={`dir-option-${f.key}-${i}`}>
            {p.name}{p.email ? ` · ${p.email}` : ""}{p.note ? ` (${p.note})` : ""}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function FieldRow({ f, intake, set, mode, dir }) {
  const v = intake[f.key];
  const business = f.filled_by === "business";
  const req = f.required ? <span className="text-[#9E382A]"> *</span> : null;
  const badge = mode === "staff" && (business || f.sync)
    ? <>
        {business && <span className="ml-1.5 inline-flex items-center gap-0.5 text-[0.55rem] px-1.5 py-0.5 rounded-full bg-[#3a4a6b]/12 text-[#3a4a6b]"><Briefcase className="h-2.5 w-2.5" />Business</span>}
        {f.sync && <span data-testid={`synced-badge-${f.key}`} title="Kept in sync with the property / owner record" className="ml-1.5 inline-flex items-center gap-0.5 text-[0.55rem] px-1.5 py-0.5 rounded-full bg-[#2B4C3F]/12 text-[#2B4C3F] normal-case tracking-normal"><Link2 className="h-2.5 w-2.5" />Synced</span>}
      </>
    : null;

  if (f.type === "info") {
    return (
      <div key={f.key} className="sm:col-span-full bg-secondary/40 border border-border rounded-md px-3 py-2.5">
        <p className="text-sm font-medium flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-accent" />{f.label}</p>
        {f.help && <p className="text-xs text-muted-foreground mt-1 whitespace-pre-line">{f.help}</p>}
      </div>
    );
  }
  if (f.type === "acknowledge") {
    return (
      <div key={f.key} className="sm:col-span-full border border-border rounded-md px-3 py-2.5 space-y-2">
        <label className="flex items-start gap-2.5 cursor-pointer">
          <Checkbox data-testid={`intake-${f.key}`} checked={!!v} onCheckedChange={(c) => set({ ...intake, [f.key]: !!c })} className="mt-0.5" />
          <span className="text-sm">{f.label}{req}{badge}{f.help && <span className="block text-xs text-muted-foreground mt-0.5">{f.help}</span>}</span>
        </label>
        {f.upload && <FileField testid={`intake-${f.key}-doc`} value={intake[`${f.key}_doc`]} onChange={(val) => set({ ...intake, [`${f.key}_doc`]: val })} />}
      </div>
    );
  }
  if (f.type === "bool") {
    return (
      <label key={f.key} className="flex items-center justify-between gap-2 border border-border rounded-md px-3 py-2">
        <span className="text-sm">{f.label}{req}{badge}</span>
        <Switch data-testid={`intake-${f.key}`} checked={!!v} onCheckedChange={(c) => set({ ...intake, [f.key]: c })} />
      </label>
    );
  }
  return (
    <div key={f.key}>
      <Label className="overline">{f.label}{req}{badge}</Label>
      {dir && <DirectoryPick f={f} dir={dir} intake={intake} set={set} />}
      {f.type === "textarea" ? (
        <Textarea data-testid={`intake-${f.key}`} rows={3} value={v || ""} placeholder={f.placeholder || ""} onChange={(e) => set({ ...intake, [f.key]: e.target.value })} className="mt-1.5" />
      ) : f.type === "select" ? (
        <Select value={v || (f.options || [])[0] || ""} onValueChange={(val) => set({ ...intake, [f.key]: val })}>
          <SelectTrigger data-testid={`intake-${f.key}`} className="mt-1.5"><SelectValue /></SelectTrigger>
          <SelectContent>{(f.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      ) : f.type === "date" ? (
        <DobPicker testid={`intake-${f.key}`} value={v || ""} onChange={(val) => set({ ...intake, [f.key]: val })} />
      ) : f.type === "file" ? (
        <FileField testid={`intake-${f.key}`} value={v} onChange={(val) => set({ ...intake, [f.key]: val })} />
      ) : (
        <Input data-testid={`intake-${f.key}`} type={f.type === "number" ? "number" : "text"} value={v || ""} placeholder={f.placeholder || ""} onChange={(e) => set({ ...intake, [f.key]: e.target.value })} className="mt-1.5" />
      )}
    </div>
  );
}

export function OnboardingFields({ schema, intake, setIntake, directors, setDirectors, mode = "landlord" }) {
  const set = setIntake;
  const [fetched, setFetched] = useState(null);
  useEffect(() => {
    if (!schema) api.get("/settings").then((r) => setFetched(r.data.onboarding_form || { sections: [], directors_enabled: true })).catch(() => setFetched({ sections: [], directors_enabled: true }));
  }, [schema]);
  const form = schema || fetched;
  const [people, setPeople] = useState({});
  const dirKinds = [...new Set((form?.sections || []).flatMap((s) => s.fields || []).map((f) => DIR_SYNC[f.sync]?.[0]).filter(Boolean))];
  useEffect(() => {
    if (mode !== "staff") return;
    dirKinds.forEach((k) => { if (!people[k]) api.get(`/directory/${k}`).then((r) => setPeople((p) => ({ ...p, [k]: r.data }))).catch(() => {}); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, dirKinds.join(",")]);
  const keyBySync = {};
  (form?.sections || []).forEach((s) => (s.fields || []).forEach((f) => { if (f.sync) keyBySync[f.sync] = f.key; }));
  const dirFor = (f) => {
    const d = DIR_SYNC[f.sync];
    if (mode !== "staff" || !d || !(people[d[0]] || []).length) return null;
    return { kind: d[0], prop: d[1], list: people[d[0]], keys: { name: keyBySync[`property.${d[0]}_name`], email: keyBySync[`property.${d[0]}_email`] } };
  };

  const addDirector = () => { if (directors.length < 5) setDirectors([...directors, { name: "", email: "", mobile: "", dob: "", place_of_birth: "" }]); };
  const updDir = (i, k, v) => setDirectors(directors.map((d, idx) => (idx === i ? { ...d, [k]: v } : d)));
  const delDir = (i) => setDirectors(directors.filter((_, idx) => idx !== i));

  if (!form) return <p className="text-sm text-muted-foreground">Loading form…</p>;

  return (
    <div className="space-y-6">
      {(form.sections || []).map((sec) => {
        const fields = (sec.fields || []).filter((f) => !(mode === "landlord" && f.filled_by === "business"));
        if (fields.length === 0) return null;
        return (
          <div key={sec.id} className="space-y-3">
            <p className="overline text-accent border-b border-border pb-1.5">{sec.title}</p>
            <div className={`grid grid-cols-1 ${sec.cols === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2"} gap-3`}>
              {fields.map((f) => <FieldRow key={f.key} f={f} intake={intake} set={set} mode={mode} dir={dirFor(f)} />)}
            </div>
          </div>
        );
      })}

      {form.directors_enabled && (
        <div className="space-y-3">
          {intake.ownership_type !== "Company" && (
            <p className="text-xs text-muted-foreground" data-testid="directors-hint">Directors are only required for <strong>Company</strong> ownership. Set ownership to "Company" above to add them.</p>
          )}
          {intake.ownership_type === "Company" && (
            <>
              <div className="flex items-center justify-between border-b border-border pb-1.5">
                <p className="overline text-accent flex items-center gap-1.5"><UserSquare2 className="h-3.5 w-3.5" />Directors ({directors.length}/5)</p>
                {directors.length < 5 && <Button type="button" size="sm" variant="outline" data-testid="add-director-button" onClick={addDirector}><Plus className="h-3.5 w-3.5 mr-1" />Add director</Button>}
              </div>
              {directors.length === 0 && <p className="text-xs text-muted-foreground">No directors added yet.</p>}
              {directors.map((d, i) => (
                <div key={i} data-testid={`director-${i}`} className="border border-border rounded-md p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-muted-foreground">Director {i + 1}</span>
                    <Button type="button" size="icon" variant="ghost" className="h-7 w-7" data-testid={`remove-director-${i}`} onClick={() => delDir(i)}><Trash2 className="h-3.5 w-3.5 text-[#9E382A]" /></Button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div><Label className="overline">Name</Label><Input data-testid={`director-name-${i}`} value={d.name || ""} onChange={(e) => updDir(i, "name", e.target.value)} className="mt-1.5" /></div>
                    <div><Label className="overline">Mobile</Label><Input data-testid={`director-mobile-${i}`} value={d.mobile || ""} onChange={(e) => updDir(i, "mobile", e.target.value)} className="mt-1.5" /></div>
                    <div><Label className="overline">Email</Label><Input data-testid={`director-email-${i}`} value={d.email || ""} onChange={(e) => updDir(i, "email", e.target.value)} className="mt-1.5" /></div>
                    <div><Label className="overline">Date of birth</Label><DobPicker testid={`director-dob-${i}`} value={d.dob || ""} onChange={(v) => updDir(i, "dob", v)} /></div>
                    <div className="sm:col-span-2"><Label className="overline">Place of birth</Label><Input data-testid={`director-pob-${i}`} value={d.place_of_birth || ""} onChange={(e) => updDir(i, "place_of_birth", e.target.value)} className="mt-1.5" /></div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
